import { useEffect, useState } from "react";
import api, { money } from "@/lib/api";
import { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Plus, Trash2, Percent } from "lucide-react";

const ADJ_LABELS = {
  percent_discount: "% discount",
  percent_increase: "% increase",
  manual: "Fixed price",
};

export function PricingRules({ scopeType, scopeId }) {
  const [rules, setRules] = useState([]);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ adjustment_type: "percent_discount", value: "", target_type: "all", target_id: null });

  const load = () => api.get("/store/pricing-rules", { params: { scope_type: scopeType, scope_id: scopeId } }).then((r) => setRules(r.data));

  useEffect(() => {
    load();
    api.get("/store/products").then((r) => setProducts(r.data));
    api.get("/store/categories").then((r) => setCategories(r.data));
    // eslint-disable-next-line
  }, [scopeType, scopeId]);

  const previewText = () => {
    const v = parseFloat(form.value);
    if (isNaN(v)) return null;
    if (form.adjustment_type === "percent_discount") {
      if (form.target_type === "product" && form.target_id) {
        const p = products.find((x) => x.id === form.target_id);
        if (p) return `${money(p.base_price)} → ${money(Math.max(0, p.base_price * (1 - v / 100)))}`;
      }
      return `Reduces the standard price by ${v}% (e.g. ${money(100)} → ${money(100 * (1 - v / 100))}).`;
    }
    if (form.adjustment_type === "percent_increase") {
      if (form.target_type === "product" && form.target_id) {
        const p = products.find((x) => x.id === form.target_id);
        if (p) return `${money(p.base_price)} → ${money(p.base_price * (1 + v / 100))}`;
      }
      return `Increases the standard price by ${v}% (e.g. ${money(100)} → ${money(100 * (1 + v / 100))}).`;
    }
    return `Sets the price to ${money(v)}.`;
  };

  const create = async () => {
    const v = parseFloat(form.value);
    if (isNaN(v) || v < 0) { toast.error("Enter a valid amount"); return; }
    if (form.adjustment_type === "percent_discount" && v > 100) { toast.error("Discount cannot exceed 100%"); return; }
    if (form.target_type !== "all" && !form.target_id) { toast.error("Select a target"); return; }
    try {
      await api.post("/store/pricing-rules", {
        scope_type: scopeType, scope_id: scopeId,
        adjustment_type: form.adjustment_type, value: v,
        target_type: form.target_type, target_id: form.target_type === "all" ? null : form.target_id,
        enabled: true,
      });
      toast.success("Pricing rule added");
      setAdding(false);
      setForm({ adjustment_type: "percent_discount", value: "", target_type: "all", target_id: null });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const toggle = async (id) => { await api.patch(`/store/pricing-rules/${id}/toggle`); load(); };
  const del = async (id) => { if (!window.confirm("Delete this rule?")) return; await api.delete(`/store/pricing-rules/${id}`); toast.success("Deleted"); load(); };

  const targetLabel = (r) => {
    if (r.target_type === "all") return "Entire catalog";
    if (r.target_type === "category") return categories.find((c) => c.id === r.target_id)?.name || "Category";
    return products.find((p) => p.id === r.target_id)?.name || "Product";
  };
  const ruleValue = (r) => r.adjustment_type === "manual" ? money(r.value) : `${r.value}%`;

  return (
    <Card className="p-6 rounded-xl border-border">
      <div className="flex items-center justify-between mb-1">
        <h3 className="font-semibold">Percentage & fixed pricing rules</h3>
        {!adding && <Button size="sm" variant="outline" onClick={() => setAdding(true)} data-testid="add-rule-btn"><Plus className="h-4 w-4 mr-1.5" /> Add rule</Button>}
      </div>
      <p className="text-sm text-muted-foreground mb-4">Adjust pricing by percentage or a fixed price. Manual per-product overrides always take priority.</p>

      {adding && (
        <div className="border border-border rounded-lg p-4 mb-4 space-y-3 bg-secondary/30">
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Adjustment</Label>
              <Select value={form.adjustment_type} onValueChange={(v) => setForm((f) => ({ ...f, adjustment_type: v }))}>
                <SelectTrigger className="h-10" data-testid="rule-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="percent_discount">Percentage discount</SelectItem>
                  <SelectItem value="percent_increase">Percentage increase</SelectItem>
                  <SelectItem value="manual">Fixed price</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">{form.adjustment_type === "manual" ? "Price" : "Percentage"}</Label>
              <Input type="number" step="0.01" value={form.value} onChange={(e) => setForm((f) => ({ ...f, value: e.target.value }))} placeholder={form.adjustment_type === "manual" ? "0.00" : "10"} className="h-10" data-testid="rule-value-input" />
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Applies to</Label>
              <Select value={form.target_type} onValueChange={(v) => setForm((f) => ({ ...f, target_type: v, target_id: null }))}>
                <SelectTrigger className="h-10" data-testid="rule-target-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Entire catalog</SelectItem>
                  <SelectItem value="category">A category</SelectItem>
                  <SelectItem value="product">A product</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {form.target_type !== "all" && (
              <div className="space-y-1.5">
                <Label className="text-xs">{form.target_type === "category" ? "Category" : "Product"}</Label>
                <Select value={form.target_id || ""} onValueChange={(v) => setForm((f) => ({ ...f, target_id: v }))}>
                  <SelectTrigger className="h-10" data-testid="rule-target-id-select"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    {(form.target_type === "category" ? categories : products).map((x) => <SelectItem key={x.id} value={x.id}>{x.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          {previewText() && <p className="text-sm text-primary font-medium" data-testid="rule-preview">{previewText()}</p>}
          <div className="flex justify-end gap-2">
            <Button size="sm" variant="ghost" onClick={() => setAdding(false)}>Cancel</Button>
            <Button size="sm" onClick={create} data-testid="save-rule-btn">Save rule</Button>
          </div>
        </div>
      )}

      {rules.length === 0 ? (
        <p className="text-sm text-muted-foreground">No pricing rules yet.</p>
      ) : (
        <div className="divide-y divide-border">
          {rules.map((r) => (
            <div key={r.id} className="flex items-center justify-between py-3" data-testid={`rule-${r.id}`}>
              <div className="flex items-center gap-3">
                <div className="h-8 w-8 rounded-lg bg-primary/10 flex items-center justify-center"><Percent className="h-4 w-4 text-primary" /></div>
                <div>
                  <p className="text-sm font-medium">{ruleValue(r)} {ADJ_LABELS[r.adjustment_type]}</p>
                  <p className="text-xs text-muted-foreground">{targetLabel(r)}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Switch checked={r.enabled} onCheckedChange={() => toggle(r.id)} data-testid={`rule-toggle-${r.id}`} />
                <button onClick={() => del(r.id)} className="text-muted-foreground hover:text-destructive" data-testid={`rule-delete-${r.id}`}><Trash2 className="h-4 w-4" /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}
